
import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useState, useMemo } from "react"; // Added useMemo
import { db } from "../firebase";

// Complete Channel Data interface
export interface CartChannelData {
  allocated?: number;
  produced?: number;
  reserved?: number;
  cancelled?: number;
  returned?: number;
  available?: number;
  sold?: number;
}

export interface CartProductData {
  id: string;
  itemId: string;
  productId: string;
  displayName: string;
  channels: {
    [channelName: string]: CartChannelData;
  };
  updatedAt?: string;
}

export interface CartSummary {
  id: string;
  cartName: string;
  date?: string | Date;
  products: {
    [productId: string]: CartProductData;
  };
  // Aggregated channel data across all products in this cart
  channels: {
    [channelName: string]: CartChannelData;
  };
}

export interface UseFirebaseCartSummariesResult {
  data: CartSummary[] | null;
  loading: boolean;
  error: Error | null;
}

// Helper function to extract number values from Firestore data
const extractNumberValue = (data: any, fieldName: string): number => {
  if (!data || data[fieldName] === undefined || data[fieldName] === null) return 0;
  
  const value = data[fieldName];
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return parseInt(value, 10);
  if (value.integerValue !== undefined) return parseInt(value.integerValue, 10);
  if (value.doubleValue !== undefined) return parseFloat(value.doubleValue);
  return 0;
};

// Helper function to extract timestamp value
const extractTimestampValue = (data: any, fieldName: string): string | undefined => {
  if (!data || !data[fieldName]) return undefined;
  
  const value = data[fieldName];
  if (typeof value === 'string') return value;
  if (value.timestampValue) return value.timestampValue;
  return undefined;
};

// Helper function to extract string values
const extractStringValue = (data: any, fieldName: string): string => {
  if (!data || !data[fieldName]) return '';
  
  const value = data[fieldName];
  if (typeof value === 'string') return value;
  if (value.stringValue) return value.stringValue;
  return '';
};

// Helper function to extract nested Firestore map values
const extractMapValue = (data: any): any => {
  if (!data) return {};
  if (data.mapValue && data.mapValue.fields) return data.mapValue.fields;
  if (typeof data === 'object' && !Array.isArray(data)) return data;
  return {};
};

export const useFirebaseCartSummaries = (
  restaurantId: string,
  date?: Date
): UseFirebaseCartSummariesResult => {
  const [data, setData] = useState<CartSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!restaurantId) {
      setLoading(false);
      setData(null);
      return;
    }

    setLoading(true);

    // Format date as YYYYMMDD
    const targetDate = date || new Date();
    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    const day = String(targetDate.getDate()).padStart(2, '0');
    const dateString = `${year}${month}${day}`;

    const cartSummariesDocRef = doc(
      db,
      'Restaurants',
      restaurantId,
      'inventories',
      dateString,
      'projections',
      'cartSummaries'
    );

    const unsubscribe = onSnapshot(
      cartSummariesDocRef,
      (docSnapshot) => {
        try {
          if (docSnapshot.exists()) {
            const docData = docSnapshot.data();

            let cartsArray: CartSummary[] = [];

            if (docData && docData.carts) {
              const cartsMap = extractMapValue(docData.carts);
              
              // Convert the carts object to an array of CartSummary objects
              cartsArray = Object.keys(cartsMap).map(cartKey => {
                const cartData = extractMapValue(cartsMap[cartKey]);
                
                // Extract cart metadata
                const cartName = extractStringValue(cartData, 'cartName');
                const cartDate = extractTimestampValue(cartData, 'date');
                
                // Parse products object
                const rawProducts = extractMapValue(cartData.products);
                const products: CartSummary['products'] = {};
                
                // Initialize aggregated channels for this cart
                const aggregatedChannels: { [channelName: string]: CartChannelData } = {};
                
                Object.keys(rawProducts).forEach(productKey => {
                  const productData = extractMapValue(rawProducts[productKey]);
                  
                  // Create product data object
                  const product: CartProductData = {
                    id: productKey,
                    itemId: extractStringValue(productData, 'itemId'),
                    productId: extractStringValue(productData, 'productId'),
                    displayName: extractStringValue(productData, 'displayName'),
                    channels: {},
                    updatedAt: extractTimestampValue(productData, 'updatedAt')
                  };
                  
                  // Extract channels for this product
                  const productChannels = extractMapValue(productData.channels);
                  
                  Object.keys(productChannels).forEach(channelKey => {
                    const channelData = extractMapValue(productChannels[channelKey]);
                    
                    // Create channel data for this product
                    const productChannelData: CartChannelData = {
                      allocated: extractNumberValue(channelData, 'allocated'),
                      reserved: extractNumberValue(channelData, 'reserved'),
                      cancelled: extractNumberValue(channelData, 'cancelled'),
                      returned: extractNumberValue(channelData, 'returned'),
                      produced: extractNumberValue(channelData, 'produced'),
                      available: extractNumberValue(channelData, 'available'),
                      sold: extractNumberValue(channelData, 'sold')
                    };
                    
                    // Add to product's channels
                    product.channels[channelKey] = productChannelData;
                    
                    // Aggregate across all products for this cart
                    if (!aggregatedChannels[channelKey]) {
                      aggregatedChannels[channelKey] = {
                        allocated: 0,
                        reserved: 0,
                        cancelled: 0,
                        returned: 0,
                        produced: 0,
                        available: 0,
                        sold: 0
                      };
                    }
                    
                    // Sum up all values for this channel across products
                    const channelAgg = aggregatedChannels[channelKey];
                    channelAgg.allocated! += productChannelData.allocated || 0;
                    channelAgg.reserved! += productChannelData.reserved || 0;
                    channelAgg.cancelled! += productChannelData.cancelled || 0;
                    channelAgg.returned! += productChannelData.returned || 0;
                    channelAgg.produced! += productChannelData.produced || 0;
                    channelAgg.available! += productChannelData.available || 0;
                    channelAgg.sold! += productChannelData.sold || 0;
                  });
                  
                  // Add product to cart's products
                  products[productKey] = product;
                });

                // Filter out channels with all zero values (optional)
                const finalChannels: CartSummary['channels'] = {};
                Object.keys(aggregatedChannels).forEach(channelKey => {
                  const channel = aggregatedChannels[channelKey];
                  // Only include channels that have at least one non-zero value
                  if (Object.values(channel).some(value => value !== 0)) {
                    finalChannels[channelKey] = channel;
                  }
                });

                return {
                  id: cartKey,
                  cartName,
                  date: cartDate,
                  products,
                  channels: finalChannels
                };
              });

            } else {
              console.log('No carts data found in cart summaries');
            }

            setData(cartsArray);
          } else {
            setData([]);
          }
          setLoading(false);
          setError(null);
        } catch (err) {
          console.error('Error parsing cart summaries document:', err);
          setError(err as Error);
          setLoading(false);
          setData([]);
        }
      },
      (err) => {
        console.error('Error fetching cart summaries document:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, date?.getTime()]);

  return { data, loading, error };
};

// Hook to get a specific cart by ID
export const useFirebaseCartSummary = (
  restaurantId: string,
  cartId: string,
  date?: Date
): {
  cartSummary: CartSummary | null;
  loading: boolean;
  error: Error | null;
  dateObject: Date;
} => {
  const { data: cartSummaries, loading, error } = useFirebaseCartSummaries(restaurantId, date);
  
  const cartSummary = useMemo(() => {
    if (!cartSummaries || !cartId) return null;
    return cartSummaries.find(cart => cart.id === cartId) || null;
  }, [cartSummaries, cartId]);

  const dateObject = useMemo(() => date || new Date(), [date]);

  return { cartSummary, loading, error, dateObject };
};

// Helper functions to work with cart summary data

// Get a specific channel property for a cart
export const getCartChannelValue = (
  cartSummary: CartSummary,
  channelName: string,
  property: keyof CartChannelData
): number => {
  return cartSummary.channels[channelName]?.[property] || 0;
};

// Get all channel properties for a specific channel in a cart
export const getCartChannelData = (
  cartSummary: CartSummary,
  channelName: string
): CartChannelData => {
  return cartSummary.channels[channelName] || {};
};

// Get product-specific channel data within a cart
export const getProductChannelData = (
  cartSummary: CartSummary,
  productId: string,
  channelName: string
): CartChannelData => {
  const product = cartSummary.products[productId];
  if (!product || !product.channels) return {};
  return product.channels[channelName] || {};
};

// Get total across all channels for a specific property in a cart
export const getCartTotalForProperty = (
  cartSummary: CartSummary,
  property: keyof CartChannelData
): number => {
  return Object.values(cartSummary.channels).reduce(
    (total, channel) => total + (channel[property] || 0),
    0
  );
};

// Get all unique channel names across all carts
export const getAllCartChannelNames = (carts: CartSummary[]): string[] => {
  const channelNames = new Set<string>();
  carts.forEach(cart => {
    Object.keys(cart.channels).forEach(channel => channelNames.add(channel));
  });
  return Array.from(channelNames);
};

// Get all products in a cart as an array
export const getCartProductsArray = (cartSummary: CartSummary): CartProductData[] => {
  return Object.values(cartSummary.products);
};

// Find a cart by cart name
export const findCartByName = (carts: CartSummary[], cartName: string): CartSummary | undefined => {
  return carts.find(cart => cart.cartName === cartName);
};

// Find a cart by cart ID
export const findCartById = (carts: CartSummary[], cartId: string): CartSummary | undefined => {
  return carts.find(cart => cart.id === cartId);
};

// Get aggregated data across all carts
export const getAggregatedCartData = (carts: CartSummary[]): {
  totalProducts: number;
  totalCarts: number;
  aggregatedChannels: { [channelName: string]: CartChannelData };
} => {
  const aggregatedChannels: { [channelName: string]: CartChannelData } = {};
  let totalProducts = 0;

  carts.forEach(cart => {
    totalProducts += Object.keys(cart.products).length;
    
    Object.keys(cart.channels).forEach(channelName => {
      if (!aggregatedChannels[channelName]) {
        aggregatedChannels[channelName] = {
          allocated: 0,
          reserved: 0,
          cancelled: 0,
          returned: 0,
          produced: 0,
          available: 0,
          sold: 0
        };
      }

      const channel = cart.channels[channelName];
      const aggChannel = aggregatedChannels[channelName];

      aggChannel.allocated! += channel.allocated || 0;
      aggChannel.reserved! += channel.reserved || 0;
      aggChannel.cancelled! += channel.cancelled || 0;
      aggChannel.returned! += channel.returned || 0;
      aggChannel.produced! += channel.produced || 0;
      aggChannel.available! += channel.available || 0;
      aggChannel.sold! += channel.sold || 0;
    });
  });

  return {
    totalProducts,
    totalCarts: carts.length,
    aggregatedChannels
  };
};

// Get all products across all carts
export const getAllProductsAcrossCarts = (carts: CartSummary[]): CartProductData[] => {
  const productsMap = new Map<string, CartProductData>();
  
  carts.forEach(cart => {
    Object.values(cart.products).forEach(product => {
      // Use itemId as unique key to avoid duplicates
      if (!productsMap.has(product.itemId)) {
        productsMap.set(product.itemId, product);
      }
    });
  });
  
  return Array.from(productsMap.values());
};